import { useFragment } from '@apollo/client'
import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { useFormik } from 'formik'
import type { FormsScheduleRecurrenceTypeEnum } from 'generated/resources/datatable.graphql'
import {
  SurveyHeaderFormFragmentDoc,
  UpdateSurveyScheduleDocument,
} from 'generated/resources/graphql'

import DayPicker from 'modules/core/components/DayPicker'
import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { firstDayOfWeekAsIndex } from 'modules/core/lib/firstDayOfWeek'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { RecurrenceSelect } from 'modules/engagement_surveys/components/RecurrenceSelect'
import {
  mapRecurrenceTypeFromBackend,
  mapRecurrenceTypeToBackend,
} from 'modules/engagement_surveys/lib/formRecurrenceMapper'

interface ScheduleSettingsForm {
  recurrenceType: FormsScheduleRecurrenceTypeEnum
  daysToAnswer?: number
  endsAt?: string
}

interface Props {
  surveyId: number
  formId: string
  onClose: () => void
}

export function ScheduleSettingsDialog({ surveyId, formId, onClose }: Props) {
  const { data: form } = useFragment({
    fragment: SurveyHeaderFormFragmentDoc,
    fragmentName: 'SurveyHeaderForm',
    from: {
      __typename: 'FormsForm',
      id: formId,
    },
  })
  const {
    access: { firstDayOfWeek },
  } = useCurrentSession()

  const [updateSurveySchedule] = useMutation(UpdateSurveyScheduleDocument, {
    extractorFn: (data) => data.surveysMutations.updateSurveysSurvey,
  })

  const formik = useFormik<ScheduleSettingsForm>({
    initialValues: {
      recurrenceType: mapRecurrenceTypeFromBackend(form.recurrenceType),
      daysToAnswer: form.configuration?.daysToAnswer ?? undefined,
      endsAt: form.endsAt || undefined,
    },
    onSubmit: async (values) => {
      const result = await updateSurveySchedule({
        variables: {
          surveyId,
          recurrenceType: mapRecurrenceTypeToBackend(values.recurrenceType),
          daysToAnswer: values.daysToAnswer,
          endsAt: values.endsAt,
          updateScheduling: true,
        },
      })

      const handled = handleResult(result, () => null)

      if (handled.success) {
        onClose()
      }
    },
  })

  return (
    <Modal
      title={i18n.t('surveys.edit_schedule_dialog.title')}
      illustration="surveys"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button
            onClick={() => formik.handleSubmit()}
            type="primary"
            loading={formik.isSubmitting}
          >
            {i18n.t('common.submit')}
          </Button>
        </>
      }
    >
      <Box gap="s16">
        <Box gap="s16">
          <RecurrenceSelect
            value={formik.values.recurrenceType}
            onChange={(recurrenceType) => {
              formik.setFieldValue('recurrenceType', recurrenceType)
              if (recurrenceType === 'onetime') {
                formik.setFieldValue('daysToAnswer', undefined)
              }
            }}
          />

          <Box flexDirection="row" alignItems="flexStart" gap="s32" width="full">
            <Box gap="s8" width="full">
              <DayPicker
                label={i18n.t('forms.schedule.end_date')}
                value={formik.values.endsAt}
                onChange={(endsAt) => {
                  formik.setFieldValue('endsAt', endsAt)
                }}
                error={formik.errors.endsAt}
                minDate={new Date()}
                firstDayOfWeek={firstDayOfWeekAsIndex(firstDayOfWeek)}
                valueFormat="backend_datetime"
              />
              <Text.Normal size="200" color="grey700">
                {i18n.t('forms.schedule.end_date_description')}
              </Text.Normal>
            </Box>

            {formik.values.recurrenceType !== 'onetime' ? (
              <TextField
                label={i18n.t('forms.schedule.days_to_answer')}
                value={
                  formik.values.daysToAnswer && isFinite(formik.values.daysToAnswer)
                    ? formik.values.daysToAnswer?.toString()
                    : ''
                }
                onChange={(value) => {
                  const daysToAnswer = value && isFinite(Number(value)) ? Number(value) : undefined

                  formik.setFieldValue('daysToAnswer', daysToAnswer)
                }}
                error={formik.errors.daysToAnswer}
              />
            ) : (
              <Box width="full" />
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}
